"use client"

import { createContext, useContext, useState, useEffect, type ReactNode, useCallback } from "react"
import { useRouter } from "next/navigation"
import {
  getCurrentUser,
  login as loginService,
  register as registerService,
  logout as logoutService,
  isUserCookieAvailable,
} from "../services/auth"
import type { Prisma } from "@prisma/client"
import { toast } from "react-hot-toast"
import { AxiosError } from "axios"

type User = Prisma.UserGetPayload<{
  include: {
    orders: {
      include: {
        shippingAddress: true
        items: true
        payment: true
        billingAddress: true
      }
    }
    addresses: true
  }
}>

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string; user?: User }>
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    promotionalOptIn: boolean,
  ) => Promise<{ success: boolean; message?: string }>
  logout: () => Promise<void>
  refreshUserData: () => Promise<User | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const auth = useProvideAuth()
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

const useProvideAuth = () => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const router = useRouter()

  const refreshUserData = useCallback(async () => {
    try {
      setIsLoading(true)
      const timestamp = new Date().getTime() // Prevent caching
      const userResponse = await getCurrentUser(`?t=${timestamp}`)
      setUser(userResponse)
      return userResponse
    } catch (error) {
      console.error("Error refreshing user data:", error)
      setUser(null)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true)

        const isCookieAvailable = await isUserCookieAvailable()

        if (!isCookieAvailable) {
          setUser(null)
          return
        }

        // Fetch protected API route on every hard refresh
        await refreshUserData()
      } catch {
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [refreshUserData])

  const handleError = useCallback((error: unknown, defaultMessage: string) => {
    let message = defaultMessage

    if (error instanceof AxiosError) {
      message = error.response?.data?.message || defaultMessage
    } else if (error instanceof Error) {
      message = error.message
    }

    console.error("Auth Error:", message)
    toast.error(message, {
      style: {
        border: "1px solid #713200",
        padding: "16px",
        color: "#713200",
      },
      iconTheme: {
        primary: "#713200",
        secondary: "#FFFAEE",
      },
    })
  }, [])

  const login = useCallback(
    async (email: string, password: string): Promise<{ success: boolean; message?: string; user?: User }> => {
      setIsLoading(true)
      try {
        const response = await loginService(email, password)
        setUser(response.user)
        toast.success("Вписахте се успешно.", {
          style: {
            border: "1px solid #713200",
            padding: "16px",
            color: "#713200",
          },
          iconTheme: {
            primary: "#713200",
            secondary: "#FFFAEE",
          },
        })
        return { success: true, user: response.user }
      } catch (error: unknown) {
        handleError(error, "Влизането не бе успешно.")
        return { success: false }
      } finally {
        setIsLoading(false)
      }
    },
    [handleError],
  )

  const register = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      promotionalOptIn: boolean,
    ): Promise<{ success: boolean; message?: string }> => {
      setIsLoading(true)
      try {
        const response = await registerService(email, password, firstName, lastName, promotionalOptIn)
        setUser(response.user)
        toast.success("Регистрацията бе успешна.", {
          style: {
            border: "1px solid #713200",
            padding: "16px",
            color: "#713200",
          },
          iconTheme: {
            primary: "#713200",
            secondary: "#FFFAEE",
          },
        })
        router.push("/customer/login")
        return { success: true }
      } catch (error: unknown) {
        handleError(error, "Регистрацията не бе успешна.")
        return { success: false }
      } finally {
        setIsLoading(false)
      }
    },
    [handleError, router],
  )

  const logout = useCallback(async () => {
    setIsLoading(true)
    try {
      await logoutService()
      setUser(null)
      toast.success("Изходът бе успешен.", {
        style: {
          border: "1px solid #713200",
          padding: "16px",
          color: "#713200",
        },
        iconTheme: {
          primary: "#713200",
          secondary: "#FFFAEE",
        },
      })
      router.push("/customer/login")
    } catch (error: unknown) {
      handleError(error, "Изходът не бе успешен.")
    } finally {
      setIsLoading(false)
    }
  }, [handleError, router])

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshUserData,
  }
}

export default AuthProvider
